/**
 * Monte Carlo Simulator Module
 * PSP/CRE Tool v3.0
 * 
 * PHASE 3: Full Monte Carlo Uncertainty Quantification
 * 
 * Implements 1000-iteration simulation varying 13 parameters to generate:
 * - Percentiles (p5, p50, p95, mean)
 * - Sensitivity analysis (variance decomposition)
 * - Threshold probabilities
 * 
 * Author: Dr. Tatar Radu MD
 * Date: February 16, 2026
 * Version: 1.1.0
 *
 * CHANGELOG:
 * v1.1.0 (April 9, 2026) - V4-002: All 13 parameters now wired to calculation engine:
 * - enemy_effectiveness  → casualtyCalculator KIA fraction shift (V4-002)
 * - climate_severity     → dnbiModule combinedModifier stack (V4-002)
 * - force_quality        → casualtyCalculator inverse KIA+WIA multiplier (V4-002)
 * - terrain_difficulty   → medevacModule daily_sorties divisor (V4-002)
 * - technology_environment → casualtyCalculator KIA-only multiplier (V4-002)
 * - resupply_success     → casualtyCalculator blood_dos initial stock scale (V4-002)
 * - equipment_readiness  → medevacModule daily_sorties + casualtyCalculator _effectiveSurgMod (V4-002)
 * - communication_effectiveness → medevacModule daily_sorties multiplier (V4-002)
 * - active_parameters updated 4 → 13
 *
 * v1.0.0 (February 16, 2026) - INITIAL IMPLEMENTATION:
 * - Implements runMonteCarloSimulation() called by casualtyCalculator.js
 * - 13 parameter variations based on research (±10% to ±30% ranges)
 * - Percentile calculation using sort-based method
 * - Sensitivity analysis via variance decomposition
 * - Threshold probability calculation
 * - Performance optimized for 1000 iterations (~5 seconds)
 * 
 * ARCHITECTURE:
 * - Called by: casualtyCalculator.js line 7709
 * - Input: { calculateFn, baseScenario, iterations, trackParameters }
 * - Output: { ...results, monte_carlo: { p5, p50, p95, mean, sensitivity, thresholds } }
 * 
 * PARAMETER VARIATIONS (13 total):
 * 1. casualty_rate_modifier (±20%) - Base battle casualty rate
 * 2. dnbi_modifier (±25%) - Disease/non-battle injury rate
 * 3. enemy_effectiveness (±25%) - Enemy morale and capability
 * 4. medevac_reliability (±30%) - MEDEVAC availability
 * 5. surgical_capacity (±20%) - Role 2/3 staffing
 * 6. golden_hour_success (±30%) - TCCC intervention timing
 * 7. climate_severity (±15%) - Weather impact on operations
 * 8. force_quality (±15%) - Training and equipment quality
 * 9. terrain_difficulty (±10%) - Terrain effects on casualties
 * 10. technology_environment (±20%) - Drone/EW environment
 * 11. resupply_success (±25%) - Logistics reliability
 * 12. equipment_readiness (±15%) - Maintenance status
 * 13. communication_effectiveness (±10%) - C2 reliability
 */

/**
 * Generate random value within ±range using normal distribution approximation
 * Uses Box-Muller transform for gaussian distribution
 * 
 * @param {number} center - Center value (1.0 for multipliers)
 * @param {number} range - ± range (e.g., 0.20 for ±20%)
 * @returns {number} Random value with normal distribution
 */
function randomNormal(center = 1.0, range = 0.20) {
  // Box-Muller transform for normal distribution
  const u1 = Math.random();
  const u2 = Math.random();
  const z0 = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
  
  // Scale to ±range (99.7% of values within ±3σ)
  const sigma = range / 3.0;
  const value = center + z0 * sigma;
  
  // Clamp to prevent extreme outliers
  const min = center - range;
  const max = center + range;
  return Math.max(min, Math.min(max, value));
}

/**
 * Generate parameter variations for one Monte Carlo iteration
 * 
 * @param {number} iteration - Current iteration number (for reproducibility)
 * @returns {Object} Parameter modifiers for this iteration
 */
function generateParameterVariations(iteration) {
  // Use iteration as seed for reproducibility (during debugging)
  // In production, pure random is fine
  
  return {
    // 1. Base casualty rate (±20%) - Primary uncertainty driver
    casualty_rate_modifier: randomNormal(1.0, 0.20),
    
    // 2. DNBI rate (±25%) - Climate, hygiene, disease factors
    dnbi_modifier: randomNormal(1.0, 0.25),
    
    // 3. Enemy effectiveness (±25%) - Morale, training, tactics
    enemy_effectiveness: randomNormal(1.0, 0.25),
    
    // 4. MEDEVAC reliability (±30%) - Weather, maintenance, availability
    medevac_reliability: randomNormal(1.0, 0.30),
    
    // 5. Surgical capacity (±20%) - Staffing, fatigue, supplies
    surgical_capacity: randomNormal(1.0, 0.20),
    
    // 6. Golden hour success (±30%) - TCCC quality, evacuation timing
    golden_hour_success: randomNormal(1.0, 0.30),
    
    // 7. Climate severity (±15%) - Temperature, precipitation effects
    climate_severity: randomNormal(1.0, 0.15),
    
    // 8. Force quality (±15%) - Training, equipment, leadership
    force_quality: randomNormal(1.0, 0.15),
    
    // 9. Terrain difficulty (±10%) - Obstacles, cover, mobility
    terrain_difficulty: randomNormal(1.0, 0.10),
    
    // 10. Technology environment (±20%) - Drone/EW dominance
    technology_environment: randomNormal(1.0, 0.20),
    
    // 11. Resupply success (±25%) - Logistics chain reliability
    resupply_success: randomNormal(1.0, 0.25),
    
    // 12. Equipment readiness (±15%) - Maintenance, spare parts
    equipment_readiness: randomNormal(1.0, 0.15),
    
    // 13. Communication effectiveness (±10%) - C2 reliability
    communication_effectiveness: randomNormal(1.0, 0.10),

    // R2-24d (2026-10-19): assumptions with NO DATA behind them, varied +/-50 percent (EDUCATED GUESS ranges, docs/research/RESEARCH_SURVIVAL_VS_TIME.md):
    // 14. late deaths (infection, sepsis, ischemia) of a casualty who waits many hours: size
    late_death_scale: randomNormal(1.0, 0.50),
    // 15. late deaths: timing (how soon they start)
    late_death_timing: randomNormal(1.0, 0.50),
    // 16. prolonged field care: share of the wait it takes off
    pfc_effect_scale: randomNormal(1.0, 0.50),
    // 17. evacuation window: how long a closure lasts
    evac_closure_scale: randomNormal(1.0, 0.50)
  };
}

/**
 * Apply parameter variations to scenario
 * Creates modified copy without mutating original
 * 
 * @param {Object} baseScenario - Original scenario configuration
 * @param {Object} params - Parameter modifiers from generateParameterVariations()
 * @returns {Object} Modified scenario for this iteration
 */
function applyParameterVariations(baseScenario, params) {
  // Deep clone to avoid mutations
  const scenario = JSON.parse(JSON.stringify(baseScenario));
  
  // Ensure monte_carlo_modifiers exists in operation_parameters
  if (!scenario.operation_parameters) {
    scenario.operation_parameters = {};
  }
  if (!scenario.operation_parameters.monte_carlo_modifiers) {
    scenario.operation_parameters.monte_carlo_modifiers = {};
  }
  
  // Apply all 13 parameter variations
  const modifiers = scenario.operation_parameters.monte_carlo_modifiers;
  
  // Parameters 1-2: Wired Phase 1.2
  modifiers.casualty_rate_modifier = params.casualty_rate_modifier;  // → battleCasualtyModule KIA/WIA
  modifiers.dnbi_modifier          = params.dnbi_modifier;           // → dnbiModule combinedModifier

  // Parameters 3-13: All wired V4-002 (April 9, 2026)
  modifiers.enemy_effectiveness         = params.enemy_effectiveness;         // V4-002 → casualtyCalculator KIA fraction shift
  modifiers.medevac_reliability         = params.medevac_reliability;         // ENH-048a → medevacModule available_aircraft
  modifiers.surgical_capacity           = params.surgical_capacity;           // ENH-048b → casualtyCalculator _effectiveSurgMod
  modifiers.golden_hour_success         = params.golden_hour_success;         // ENH-048c → casualtyCalculator _mcGoldenHourDOWMod
  modifiers.climate_severity            = params.climate_severity;            // V4-002 → dnbiModule combinedModifier
  modifiers.force_quality               = params.force_quality;               // V4-002 → casualtyCalculator inverse KIA+WIA
  modifiers.terrain_difficulty          = params.terrain_difficulty;          // V4-002 → medevacModule daily_sorties divisor
  modifiers.technology_environment      = params.technology_environment;      // V4-002 → casualtyCalculator KIA-only multiplier
  modifiers.resupply_success            = params.resupply_success;            // V4-002 → casualtyCalculator blood_dos init scale
  modifiers.equipment_readiness         = params.equipment_readiness;         // V4-002 → medevacModule sorties + casualtyCalculator _effectiveSurgMod
  modifiers.communication_effectiveness = params.communication_effectiveness; // V4-002 → medevacModule daily_sorties multiplier

  // R2-24d: tail assumptions
  modifiers.late_death_scale   = params.late_death_scale;    // → woundHazardModule weibull components (size)
  modifiers.late_death_timing  = params.late_death_timing;   // → woundHazardModule weibull components (timing)
  modifiers.pfc_effect_scale   = params.pfc_effect_scale;    // → evacuationWindowModel.pfcEffectiveWait
  modifiers.evac_closure_scale = params.evac_closure_scale;  // → evacuationWindowModel closure duration
  
  return scenario;
}

/**
 * Calculate percentile from sorted array
 * 
 * @param {Array<number>} sortedArray - Sorted values
 * @param {number} percentile - Percentile to find (0-100)
 * @returns {number} Value at percentile
 */
function calculatePercentile(sortedArray, percentile) {
  if (sortedArray.length === 0) return 0;
  if (sortedArray.length === 1) return sortedArray[0];
  
  const index = (percentile / 100) * (sortedArray.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;
  
  if (lower === upper) {
    return sortedArray[lower];
  }
  
  return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
}

/**
 * Calculate mean of array
 * 
 * @param {Array<number>} values - Values to average
 * @returns {number} Mean value
 */
function calculateMean(values) {
  if (values.length === 0) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}

/**
 * Calculate variance of array
 * 
 * @param {Array<number>} values - Values
 * @param {number} mean - Pre-calculated mean
 * @returns {number} Variance
 */
function calculateVariance(values, mean) {
  if (values.length === 0) return 0;
  return values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
}

/**
 * Perform sensitivity analysis via variance decomposition
 * Calculates how much each parameter contributes to output variance
 * 
 * @param {Array<Object>} results - All iteration results
 * @param {Array<Object>} parameterSets - All parameter variations
 * @returns {Array<Object>} Sensitivity factors sorted by impact
 */
function calculateSensitivityAnalysis(results, parameterSets, outputOf = (r) => r.by_corps?.total_all_sources || r.by_corps?.total_casualties || 0) {
  // Extract the output (default: total casualties) from each iteration
  // INC-007: total_all_sources is the canonical field (built at casualtyCalculator §7628);
  // total_casualties is a legacy field name kept as defensive fallback only.
  const casualties = results.map(outputOf);
  const totalMean = calculateMean(casualties);
  const totalVariance = calculateVariance(casualties, totalMean);
  
  if (totalVariance === 0) {
    return []; // No variance to decompose
  }
  
  // Calculate correlation between each parameter and output
  const parameterNames = [
    { key: 'casualty_rate_modifier', name: 'Base Casualty Rate', range: 0.20 },
    { key: 'dnbi_modifier', name: 'DNBI Rate', range: 0.25 },
    { key: 'enemy_effectiveness', name: 'Enemy Effectiveness', range: 0.25 },
    { key: 'medevac_reliability', name: 'MEDEVAC Availability', range: 0.30 },
    { key: 'surgical_capacity', name: 'Surgical Capacity', range: 0.20 },
    { key: 'golden_hour_success', name: 'Golden Hour Success', range: 0.30 },
    { key: 'climate_severity', name: 'Climate Conditions', range: 0.15 },
    { key: 'force_quality', name: 'Force Quality', range: 0.15 },
    { key: 'terrain_difficulty', name: 'Terrain Effects', range: 0.10 },
    { key: 'technology_environment', name: 'Technology Environment', range: 0.20 },
    { key: 'resupply_success', name: 'Resupply Reliability', range: 0.25 },
    { key: 'equipment_readiness', name: 'Equipment Readiness', range: 0.15 },
    { key: 'communication_effectiveness', name: 'Communication Effectiveness', range: 0.10 },
    { key: 'late_death_scale', name: 'Late deaths without surgery (size)', range: 0.50 },
    { key: 'late_death_timing', name: 'Late deaths without surgery (timing)', range: 0.50 },
    { key: 'pfc_effect_scale', name: 'Prolonged field care effect', range: 0.50 },
    { key: 'evac_closure_scale', name: 'Evacuation closure duration', range: 0.50 }
  ];
  
  const sensitivities = parameterNames.map(({ key, name, range }) => {
    // Get parameter values across all iterations
    const paramValues = parameterSets.map(p => p[key]);
    const paramMean = calculateMean(paramValues);
    
    // Calculate covariance between parameter and casualties
    let covariance = 0;
    for (let i = 0; i < paramValues.length; i++) {
      covariance += (paramValues[i] - paramMean) * (casualties[i] - totalMean);
    }
    covariance /= paramValues.length;
    
    // Calculate parameter variance
    const paramVariance = calculateVariance(paramValues, paramMean);
    
    // Sensitivity = (covariance / paramVariance) if paramVariance > 0
    let sensitivity = 0;
    if (paramVariance > 0) {
      const slope = covariance / paramVariance;
      // Convert to percentage of total variance explained
      sensitivity = Math.abs((slope * paramVariance) / totalVariance) * 100;
    }
    
    // Explanation based on parameter
    const explanations = {
      'Base Casualty Rate': 'Primary driver of outcome variance - enemy firepower and tactics',
      'DNBI Rate': 'Disease and non-battle injuries vary by climate and hygiene',
      'Enemy Effectiveness': 'Enemy morale, training, and equipment quality fluctuations',
      'MEDEVAC Availability': 'Weather, maintenance, and tactical situation affect evacuation',
      'Surgical Capacity': 'Surgeon fatigue and staff availability during sustained ops',
      'Golden Hour Success': 'TCCC intervention timing varies by unit positioning',
      'Climate Conditions': 'Temperature extremes and precipitation impact operations',
      'Force Quality': 'Unit training levels and equipment state affect survivability',
      'Terrain Effects': 'Cover, obstacles, and mobility vary across operational area',
      'Technology Environment': 'Drone/EW dominance shifts during multi-day operations',
      'Resupply Reliability': 'Logistics chain disruptions affect medical supply DOS',
      'Equipment Readiness': 'Maintenance and spare parts availability fluctuates',
      'Communication Effectiveness': 'C2 reliability varies by terrain and enemy action',
      'Late deaths without surgery (size)': 'No data: how many casualties die of infection or sepsis when they wait many hours (assumption varied +/-50%)',
      'Late deaths without surgery (timing)': 'No data: how soon those late deaths start (assumption varied +/-50%)',
      'Prolonged field care effect': 'No data: how much of the wait prolonged field care takes off (assumption varied +/-50%)',
      'Evacuation closure duration': 'No data: how long an evacuation window stays closed (assumption varied +/-50%)'
    };
    
    return {
      factor_name: name,
      impact_percent: Math.min(100, sensitivity), // Cap at 100%
      explanation: explanations[name] || 'Contributes to outcome uncertainty'
    };
  });
  
  // Sort by impact (highest first) and return top factors
  return sensitivities
    .sort((a, b) => b.impact_percent - a.impact_percent)
    .filter(s => s.impact_percent > 0.5); // Only include meaningful factors
}

/**
 * Calculate threshold probabilities
 * Shows probability of exceeding specific casualty counts
 * Generates 3 thresholds spread across actual variance range
 */
function calculateThresholdProbabilities(results, deterministicTotal) {
  // INC-007: total_all_sources is canonical; total_casualties is legacy fallback
  const casualties = results.map(r => r.by_corps?.total_all_sources || r.by_corps?.total_casualties || 0);
  casualties.sort((a, b) => a - b);
  
  const base = deterministicTotal || calculateMean(casualties);
  const p5val  = calculatePercentile(casualties, 95); // worst
  const p95val = calculatePercentile(casualties, 5);  // best
  const range  = p5val - p95val;
  
  // Generate 3 thresholds spread across variance range
  // Only add if they differ meaningfully (>1% of base)
  const candidates = [
    Math.round(base * 1.05),           // 5% above deterministic
    Math.round(base + range * 0.33),   // 1/3 into worst range
    Math.round(base + range * 0.66),   // 2/3 into worst range
    Math.round(p5val)                  // worst case boundary
  ];
  
  // Deduplicate and keep only distinct thresholds (>50 apart)
  const thresholds = [];
  for (const candidate of candidates) {
    if (candidate > base && !thresholds.some(t => Math.abs(t - candidate) < 50)) {
      thresholds.push(candidate);
    }
  }
  
  return thresholds.map(threshold => {
    const exceedCount = casualties.filter(c => c > threshold).length;
    const probability = (exceedCount / casualties.length) * 100;
    return {
      threshold_value: threshold,
      probability_percent: Math.round(probability)
    };
  }).filter(t => t.probability_percent > 1 && t.probability_percent < 99);
}

/**
 * Main Monte Carlo simulation function
 * Called by casualtyCalculator.js
 * 
 * @param {Object} config - Configuration object
 * @param {Function} config.calculateFn - Calculator function to call
 * @param {Object} config.baseScenario - Base scenario configuration
 * @param {number} config.iterations - Number of iterations (default 1000)
 * @param {boolean} config.trackParameters - Whether to track parameters (default true)
 * @returns {Object} Results with monte_carlo statistics
 */
export function runMonteCarloSimulation({ 
  calculateFn, 
  baseScenario, 
  iterations = 1000, 
  trackParameters = true,
  deterministicResult = null
}) {
  const startTime = Date.now();
  
  // Storage for all iteration results
  const allResults = [];
  const allParameters = [];
  
  // Run iterations
  for (let i = 0; i < iterations; i++) {
    // Generate parameter variations for this iteration
    const params = generateParameterVariations(i);
    
    // Apply variations to scenario
    const modifiedScenario = applyParameterVariations(baseScenario, params);
    
    // Run calculator with modified scenario
    // Pass options={ final: false } to prevent recursive Monte Carlo
    const result = calculateFn(modifiedScenario, { final: false });
    
    // Store results
    allResults.push(result);
    if (trackParameters) {
      allParameters.push(params);
    }
    
  }
  
  const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(1);

  // Extract casualty metrics from all results
  // INC-007: total_all_sources is canonical; total_casualties is legacy fallback
  const extractMetrics = (result) => ({
    total_casualties: result.by_corps?.total_all_sources || result.by_corps?.total_casualties || 0,
    total_kia: result.by_corps?.total_kia || 0,
    total_wia: result.by_corps?.total_wia || 0,
    total_dow: result.by_corps?.total_dow || 0,
    total_dnbi: result.by_corps?.total_dnbi || 0,
    total_rtd: result.by_corps?.total_rtd || 0,
    // R2-24d: deaths before surgery = killed before arrival + died of wounds
    total_deaths_before_surgery: (result.by_corps?.total_prehospital_deaths || 0) + (result.by_corps?.total_dow || 0)
  });
  
  // Sort ALL metrics TOGETHER by total_casualties
  // Ensures KIA/WIA/DNBI all come from the SAME scenario iteration
  const allMetrics = allResults.map(extractMetrics);
  allMetrics.sort((a, b) => a.total_casualties - b.total_casualties);
  
  const getIdx = (percentile) => 
    Math.min(allMetrics.length - 1, Math.round((percentile / 100) * (allMetrics.length - 1)));

  // MILITARY CONVENTION: p5=worst(high casualties), p95=best(low casualties)
  const p5  = { ...allMetrics[getIdx(95)] }; // WORST: 5% chance higher
  const p95 = { ...allMetrics[getIdx(5)]  }; // BEST: 95% chance lower

  // p50 = deterministic result (guaranteed match with summary cards)
  // Falls back to statistical median only if deterministic not available
  const p50 = deterministicResult
    ? extractMetrics(deterministicResult)
    : { ...allMetrics[getIdx(50)] };

  // Mean across all iterations
  const n = allMetrics.length;
  const mean = {
    total_casualties: Math.round(allMetrics.reduce((s, m) => s + m.total_casualties, 0) / n),
    total_kia:        Math.round(allMetrics.reduce((s, m) => s + m.total_kia, 0) / n),
    total_wia:        Math.round(allMetrics.reduce((s, m) => s + m.total_wia, 0) / n),
    total_dow:        Math.round(allMetrics.reduce((s, m) => s + m.total_dow, 0) / n),
    total_dnbi:       Math.round(allMetrics.reduce((s, m) => s + m.total_dnbi, 0) / n),
    total_rtd:        Math.round(allMetrics.reduce((s, m) => s + m.total_rtd, 0) / n)
  };
  
  const sensitivity = calculateSensitivityAnalysis(allResults, allParameters);
  // R2-24d: the range of the deaths before surgery (its own sort: the worst case of the deaths is not the worst case of the casualties)
  const dbs = allResults.map((r) => (r.by_corps?.total_prehospital_deaths || 0) + (r.by_corps?.total_dow || 0)).sort((a, b) => a - b);
  const dbsAt = (pct) => dbs[Math.min(dbs.length - 1, Math.round((pct / 100) * (dbs.length - 1)))];
  const dbsDet = deterministicResult ? (deterministicResult.by_corps?.total_prehospital_deaths || 0) + (deterministicResult.by_corps?.total_dow || 0) : dbsAt(50);
  const deaths_before_surgery = {
    p5: dbsAt(95), p50: dbsDet, p95: dbsAt(5),   // p5 = worst (most deaths), as for the casualties
    mean: Math.round(dbs.reduce((s, v) => s + v, 0) / dbs.length),
    sensitivity: calculateSensitivityAnalysis(allResults, allParameters, (r) => (r.by_corps?.total_prehospital_deaths || 0) + (r.by_corps?.total_dow || 0)),
    status: 'EDUCATED_GUESS',
    basis: 'Includes four assumptions with no data (late deaths size and timing, prolonged field care effect, evacuation closure duration), each varied +/-50 percent.'
  };
  const thresholds = calculateThresholdProbabilities(allResults, p50.total_casualties);

  // Base result = deterministic (preserves all non-MC fields: supply chain, validation etc.)
  const baseResult = deterministicResult || allResults[getIdx(50)];
  
  return {
    ...baseResult,
    monte_carlo: {
      iterations,
      p5,
      p50,
      p95,
      mean,
      sensitivity,
      deaths_before_surgery,
      thresholds,
      // BUG-MC-001 FIX: 4 parameters wired — casualty_rate_modifier (v5.41.0),
      // dnbi_modifier (original), surgical_capacity (ENH-048b), golden_hour_success (ENH-048c).
      active_parameters: 17,
      total_parameters: 17,
      calculated_at: new Date().toISOString(),
      computation_time_seconds: parseFloat(elapsedTime)
    }
  };
}

/**
 * Export for testing
 */
export {
  generateParameterVariations,
  applyParameterVariations,
  calculatePercentile,
  calculateSensitivityAnalysis,
  calculateThresholdProbabilities
};
